import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'utils/axios';
import { jwtDecode } from 'jwt-decode';
import { KeyedObject } from 'types';

// ==============================|| INITIAL STATE ||============================== //

interface AuthUser {
  id: string;
  organizationId: string | null;
  email: string;
  name: string;
  role: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  organization?: any;
  oauthProvider?: string | null; // 'github' | 'gitlab' | 'microsoft' | 'google' | null
  hasPassword?: boolean; // Whether user has a password set (false for OAuth-only users who haven't set one)
  // Legal consent fields
  tosAcceptedAt?: string | null;
  tosVersion?: string | null;
  privacyPolicyAcceptedAt?: string | null;
  privacyPolicyVersion?: string | null;
  marketingConsent?: boolean;
  marketingConsentAt?: string | null;
}

interface PolicyConfig {
  currentTosVersion: string;
  currentPrivacyPolicyVersion: string;
  tosUrl: string;
  privacyPolicyUrl: string;
  needsConsent: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser | null;
  policyConfig: PolicyConfig | null;
  accessToken: string | null;
  refreshToken: string | null;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  policyConfig: null,
  accessToken: null,
  refreshToken: null,
  status: 'idle',
  error: '',
};

// ==============================|| HELPERS ||============================== //

function verifyToken(serviceToken: string): boolean {
  if (!serviceToken) {
    return false;
  }

  const decoded: KeyedObject = jwtDecode(serviceToken);

  if (!decoded.exp) {
    throw new Error("Token does not contain 'exp' property.");
  }

  return decoded.exp > Date.now() / 1000;
}

function setSession(serviceToken?: string | null): void {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
}

// ==============================|| ASYNC THUNKS ||============================== //

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, turnstileToken }: { email: string; password: string; turnstileToken?: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/auth/login', { email, password, turnstileToken });
      const { user, tokens } = response.data.response || response.data;

      // Store access token only (refresh token is set as HTTP-only cookie by backend)
      setSession(tokens.accessToken);

      return { user, tokens };
    } catch (error: any) {
      // Check if turnstile is required
      if (error?.error?.turnstileRequired || error?.turnstileRequired) {
        return rejectWithValue({ message: 'Security verification required', turnstileRequired: true });
      }
      return rejectWithValue(error?.error?.message || error?.message || 'Login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async ({ email, password, firstName, lastName, turnstileToken }: { email: string; password: string; firstName: string; lastName: string; turnstileToken: string }, { rejectWithValue }) => {
    try {
      const name = `${firstName} ${lastName}`;
      const response = await axios.post('/auth/register', {
        email,
        password,
        name,
        turnstileToken
      });

      const data = response.data.response || response.data;

      // Registration no longer returns tokens - user must verify email first
      return { message: data.message };
    } catch (error: any) {
      return rejectWithValue('Something went wrong. Please try again later.');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      // Call backend to invalidate all tokens and clear HTTP-only cookie
      await axios.post('/auth/logout');
    } catch (error) {
      // Even if backend call fails, still clear local access token
      console.error('Failed to call logout endpoint:', error);
    }

    // Clear access token from localStorage (refresh token cookie cleared by backend)
    setSession(null);
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      let serviceToken = window.localStorage.getItem('serviceToken');

      // If we have a valid access token, use it
      if (serviceToken && verifyToken(serviceToken)) {
        setSession(serviceToken);

        const response = await axios.get('/auth/me');
        const data = response.data.response || response.data;
        const { user, policyConfig } = data;

        return { user, policyConfig, accessToken: serviceToken };
      }

      // No valid access token - try to refresh using HTTP-only cookie
      // This handles page refresh when access token expired but refresh token cookie exists
      try {
        const refreshResponse = await axios.post('/auth/refresh', {});
        const { tokens } = refreshResponse.data.response || refreshResponse.data;

        // Store new access token
        setSession(tokens.accessToken);

        // Now get user profile
        const meResponse = await axios.get('/auth/me');
        const data = meResponse.data.response || meResponse.data;
        const { user, policyConfig } = data;

        return { user, policyConfig, accessToken: tokens.accessToken };
      } catch (refreshError) {
        // Refresh failed - user is not authenticated
        // This is normal if user never logged in or cookie expired
        return null;
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (_, { rejectWithValue }) => {
    try {
      const serviceToken = window.localStorage.getItem('serviceToken');
      if (serviceToken && verifyToken(serviceToken)) {
        const response = await axios.get('/auth/me');
        const data = response.data.response || response.data;
        // New response structure: { user, policyConfig }
        const { user, policyConfig } = data;
        return { user, policyConfig };
      }
      return null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const acceptConsent = createAsyncThunk(
  'auth/acceptConsent',
  async (
    { acceptTos, acceptPrivacyPolicy, marketingConsent }: { acceptTos: boolean; acceptPrivacyPolicy: boolean; marketingConsent?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post('/auth/consent', {
        acceptTos,
        acceptPrivacyPolicy,
        marketingConsent: marketingConsent ?? false,
      });
      const data = response.data.response || response.data;
      const { user, policyConfig } = data;
      return { user, policyConfig };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, turnstileToken }: { email: string; turnstileToken: string }, { rejectWithValue }) => {
    try {
      await axios.post('/auth/forgot-password', { email, turnstileToken });
      return { message: 'Password reset email sent' };
    } catch (error: any) {
      return rejectWithValue(error?.error?.message || error?.message || 'Failed to send reset email');
    }
  }
);

export const confirmResetPassword = createAsyncThunk(
  'auth/confirmResetPassword',
  async ({ token, newPassword }: { token: string; newPassword: string }, { rejectWithValue }) => {
    try {
      await axios.post('/auth/reset-password', { token, newPassword });
      return { message: 'Password has been reset successfully' };
    } catch (error: any) {
      return rejectWithValue(error?.error?.message || error?.message || 'Failed to reset password');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }, { rejectWithValue }) => {
    try {
      await axios.post('/auth/change-password', { currentPassword, newPassword });
      return { message: 'Password changed successfully' };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const verifyInvite = createAsyncThunk(
  'auth/verifyInvite',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/auth/verify-invite?token=${token}`);
      return response.data.response || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const acceptInvite = createAsyncThunk(
  'auth/acceptInvite',
  async ({ token, password }: { token: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/auth/accept-invite', { token, password });
      const { user, tokens } = response.data.response || response.data;

      // Store access token only (refresh token is set as HTTP-only cookie by backend)
      if (tokens?.accessToken) {
        setSession(tokens.accessToken);
      }

      return { user, tokens };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ==============================|| SLICE ||============================== //

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload) {
          state.isAuthenticated = true;
          state.isInitialized = true;
          state.user = action.payload.user;
          state.accessToken = action.payload.tokens.accessToken;
          state.refreshToken = action.payload.tokens.refreshToken;
          state.status = 'success';
          state.error = '';
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Login failed';
        state.isInitialized = true;
      });

    // Register (no longer auto-authenticates - user must verify email)
    builder
      .addCase(register.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(register.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
      })
      .addCase(register.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Registration failed';
      });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.status = 'idle';
      state.error = '';
    });

    // Initialize
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isInitialized = true;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.policyConfig = action.payload.policyConfig || null;
          state.accessToken = action.payload.accessToken;
          state.refreshToken = action.payload.refreshToken;
        }
        state.status = 'success';
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isInitialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.policyConfig = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.status = 'failed';
      });

    // Update Profile
    builder
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.policyConfig = action.payload.policyConfig || null;
        }
      });

    // Accept Consent
    builder
      .addCase(acceptConsent.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(acceptConsent.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user;
          state.policyConfig = action.payload.policyConfig;
        }
        state.status = 'success';
      })
      .addCase(acceptConsent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to record consent';
      });

    // Accept Invite (logs user in)
    builder
      .addCase(acceptInvite.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(acceptInvite.fulfilled, (state, action) => {
        if (action.payload) {
          state.isAuthenticated = true;
          state.isInitialized = true;
          state.user = action.payload.user;
          state.accessToken = action.payload.tokens.accessToken;
          state.refreshToken = action.payload.tokens.refreshToken;
          state.status = 'success';
          state.error = '';
        }
      })
      .addCase(acceptInvite.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to accept invitation';
        state.isInitialized = true;
      });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError } = authSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectIsAuthenticated = (state: any) => state.auth.isAuthenticated;
export const selectIsInitialized = (state: any) => state.auth.isInitialized;
export const selectAuthUser = (state: any) => state.auth.user;
export const selectPolicyConfig = (state: any) => state.auth.policyConfig;
export const selectNeedsConsent = (state: any) => state.auth.policyConfig?.needsConsent ?? false;
export const selectAuthStatus = (state: any) => state.auth.status;
export const selectAuthError = (state: any) => state.auth.error;

export default authSlice.reducer;
